'use client';

import { useEffect, useMemo, useTransition } from 'react';
import { Controller, useForm, type SubmitHandler } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { isEmpty, map, trim } from 'lodash';
import { useHookFormMask } from 'use-mask-input';
import { useMediaQuery } from 'usehooks-ts';
import Button from '@/components/shared/Button';
import { FormStatus } from '@/components/ui/FormStatus';
import { ArrowLeftIcon } from '@/icons';
import { onlyNumbers } from '@/utils/onlyNumbers';
import { Input, Select, TextArea } from '@axa/design-system';
import { zodResolver } from '@hookform/resolvers/zod';
import { preRegistrationSchema, type PreRegistrationFormData } from './schema';

type PreRegistrationContainerProps = {
  title?: string;
  description?: string;
  motivationList?: {
    id: string;
    label: string;
    withDescriptionField: boolean;
  }[];
};

export const PreRegistrationContainer = ({
  title,
  description,
  motivationList
}: PreRegistrationContainerProps) => {
  const router = useRouter();
  const isMobile = useMediaQuery('(max-width: 1023px)');

  const [isPending, startTransition] = useTransition();

  const motivationOptions = useMemo(
    () =>
      map(motivationList, (item) => ({
        label: item.label,
        value: item.id,
        withDescriptionField: item.withDescriptionField
      })),
    [motivationList]
  );

  const {
    control,
    formState: { errors, isSubmitting, dirtyFields },
    register,
    handleSubmit,
    reset,
    resetField,
    watch
  } = useForm<PreRegistrationFormData>({
    resolver: zodResolver(preRegistrationSchema)
  });
  const registerWithMask = useHookFormMask(register);

  const handleSubmitPreRegistration: SubmitHandler<PreRegistrationFormData> = async (formData) => {
    startTransition(async () => {
      // TODO quando tiver RD Station, alterar para salvar lead
      await new Promise((resolve) => setTimeout(resolve, 1000));

      let successUrl = '/para-corretores/pre-cadastro/sucesso';

      if (!isMobile) {
        window.open(
          `${process.env.NEXT_PUBLIC_VAYON_URL}/#!/cadastro-externo/${onlyNumbers(formData.cnpj)}/${trim(formData.email)}`,
          '_blank',
          'noopener,noreferrer'
        );
      } else {
        successUrl += `?cnpj=${onlyNumbers(formData.cnpj)}&email=${trim(formData.email)}`;
      }
      reset();
      router.push(successUrl);
    });
  };

  const motivation = watch('motivation');

  useEffect(() => {
    if (!motivation?.withDescriptionField) {
      resetField('motivationDescription');
    }
  }, [motivation?.withDescriptionField, resetField]);

  return (
    <section className="relative grid min-h-[calc(100vh_-_120px)] grid-rows-[auto_1fr] gap-10 lg:grid-cols-2 lg:grid-rows-1 lg:gap-0 xl:h-[calc(100vh_-_128px)]">
      {isPending && (
        <FormStatus
          status="loading"
          title="Você está sendo redirecionado..."
          className="absolute"
        />
      )}
      <div className="flex w-full items-center bg-white px-6 pt-6 lg:bg-grey-100 lg:px-12 lg:pt-0 xl:px-20 2xl:px-[7.5rem]">
        <div className="flex max-w-[calc(100vw_-_3rem)] flex-col gap-2 lg:max-w-[416px] lg:gap-4 xl:max-w-[480px]">
          <button
            type="button"
            onClick={router.back}
            className="flex h-12 items-center gap-2 text-sm font-semibold uppercase leading-4 tracking-[1px] text-grey-900 hover:underline lg:hidden"
          >
            <ArrowLeftIcon />
            Voltar
          </button>
          <h1 className="break-words font-publico text-2xl font-bold text-blue-400 lg:text-[2rem] lg:leading-10 xl:text-[2.5rem] xl:leading-[3rem]">
            {title}
          </h1>
          {description && (
            <p className="break-words text-base font-regular text-grey-700">{description}</p>
          )}
        </div>
      </div>
      <form
        onSubmit={handleSubmit(handleSubmitPreRegistration)}
        className="flex w-full flex-col bg-white px-6 pb-12 lg:px-12 lg:pb-0 xl:px-20 3xl:px-[7.5rem]"
      >
        <div className="flex flex-1 flex-col justify-between gap-6 lg:justify-center lg:gap-8 xl:max-w-[480px] 2xl:max-w-[560px] 3xl:max-w-[720px]">
          <div className="flex flex-col gap-6 lg:gap-8">
            <Input
              id="cnpj"
              inputMode="numeric"
              label="CNPJ"
              placeholder="Informe seu CNPJ"
              leftIcon={{
                name: 'FileMinus'
              }}
              errorMessage={errors.cnpj?.message}
              hasError={!!errors.cnpj}
              {...registerWithMask('cnpj', ['99.999.999/9999-99'])}
            />
            <Input
              id="email"
              inputMode="email"
              label="E-mail"
              placeholder="Informe seu e-mail"
              leftIcon={{
                name: 'Mail'
              }}
              errorMessage={errors.email?.message}
              hasError={!!errors.email}
              {...register('email')}
            />
            <Controller
              control={control}
              name="motivation"
              render={({ field }) => (
                <Select
                  id="motivation"
                  label="O que te motivou a se tornar um corretor AXA?"
                  errorMessage={errors.motivation?.message}
                  hasError={!!errors.motivation}
                  options={motivationOptions}
                  searchable={false}
                  placeholder="Selecione..."
                  {...field}
                />
              )}
            />
            {motivation?.withDescriptionField && (
              <TextArea
                id="motivationDescription"
                label="Outro motivo"
                placeholder="Informe o motivo pelo qual quer se tornar um corretor AXA"
                errorMessage={errors.motivationDescription?.message}
                hasError={!!errors.motivationDescription}
                {...register('motivationDescription')}
              />
            )}
          </div>
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={router.back}
              className="hidden h-12 text-sm font-semibold uppercase leading-4 tracking-[1px] text-grey-900 hover:underline lg:flex lg:items-center lg:gap-2"
            >
              <ArrowLeftIcon />
              Voltar
            </button>
            <Button
              type="submit"
              disabled={isSubmitting || isEmpty(dirtyFields)}
              className="w-full justify-center lg:w-fit"
            >
              Continuar
            </Button>
          </div>
        </div>
      </form>
    </section>
  );
};
